import { BookingCreationDtoFactory } from "@/views/bookingForm/bookingCreationDtoFactory";
import {BusinessBookerEventCode} from "@/core/tracking/businessBookerEvent/BusinessBookerEventCode";
import {SafeAwait} from "@/core/util/SafeAwait";
import {BusinessBookerEvent} from "@/core/tracking/businessBookerEvent/BusinessBookerEvent";
import {HiDash} from "@/core/util/HiDash";

export class BookingFormInteractor {
  /**
   *
   * @param $store
   * @param {BookingFormModelDto} bookingFormModel
   */
  constructor({ $store, bookingFormModel }) {
    this.$store = $store;
    this.bookingFormModel = bookingFormModel;
  }

  setOffer(offer) {
    this.bookingFormModel.offerId = offer.id;
    this.bookingFormModel.offer = offer;
  }

  setBusinessAccountId(businessAccountId) {
    this.bookingFormModel.businessAccountId = businessAccountId;
  }

  /**
   *
   * @param {AddressItemDetailApiDto} pickupAddress
   */
  setPickupAddress(pickupAddress) {
    this.bookingFormModel.origin = pickupAddress;
  }

  /**
   * @param {AirportDetails} airportDetails
   */
  setAirportDetails(airportDetails) {
    this.bookingFormModel.airportDetails = airportDetails;
  }

  /**
   * @param {AddressItemDetailApiDto} destinationAddress
   */
  setDestinationAddress(destinationAddress) {
    this.bookingFormModel.destination = destinationAddress;
  }

  /**
   * @param {AddressItemDetailApiDto[]} waypoints
   */
  setWaypoints(waypoints) {
    this.bookingFormModel.waypoints = waypoints;
  }

  setFlightNumber(flightNumber) {
    this.bookingFormModel.flightNumber = flightNumber;
  }

  setReturnFlightNumber(returnFlightNumber) {
    this.bookingFormModel.returnFlightNumber = returnFlightNumber;
  }

  /**
   *
   * @param {Moment|null} dateTime
   */
  setPickupTime(dateTime) {
    this.bookingFormModel.pickupTime = dateTime;
  }

  /**
   * @param {Moment|null} returnTime
   */
  setReturnBookingTime(returnTime) {
    this.bookingFormModel.returnBookingTime = returnTime;
  }

  /**
   * @param {String} scheduleType
   */
  setScheduleType(scheduleType) {
    this.bookingFormModel.scheduleType = scheduleType;
  }

  setPassenger(passenger) {
    this.bookingFormModel.passenger = passenger;
  }

  /**
   * @param {PaymentMethodDomainModel} paymentMethod
   */
  setPaymentMethod(paymentMethod) {
    this.bookingFormModel.paymentMethod = paymentMethod;
  }

  /**
   * @param {string} fleetId
   */
  setFleetId(fleetId) {
    this.bookingFormModel.fleetId = fleetId;
  }

  /**
   * @param {number} numberOfVehicles
   */
  setNumberOfVehicles(numberOfVehicles) {
    this.bookingFormModel.numberOfVehicles = numberOfVehicles;
  }

  /**
   * @param {boolean} shouldBookReturnBooking
   */
  setShouldBookReturnBooking(shouldBookReturnBooking) {
    this.bookingFormModel.shouldBookReturnBooking = shouldBookReturnBooking;
  }

  /**
   * @param {string} offerId
   */
  setOfferId(offerId) {
    this.bookingFormModel.offerId = offerId;
  }

  setAccountFields(accountFields) {
    this.bookingFormModel.fields = accountFields;
  }

  setBookingNotes(notes) {
    this.bookingFormModel.notes = notes;
  }

  /**
   * @param {OfferAttributeExtraDto[]|null}  extras
   */
  setSelectedAttributeExtras(extras) {
    if (extras && Array.isArray(extras)) {
      this.bookingFormModel.attributeExtras = extras
        .map((dtoExtra) => dtoExtra.code);
    }
  }

  setDuplicateBooking(isDuplicateBooking) {
    this.bookingFormModel.isDuplicateBooking = isDuplicateBooking;
  }

  setBookingCopyDetails(bookingCopyDetails) {
    this.bookingFormModel.bookingCopyDetails = bookingCopyDetails;
  }

  canFetchOffers() {
    return (
      !!this.bookingFormModel.origin &&
      !!this.bookingFormModel.destination &&
      !!this.bookingFormModel.paymentMethod
    );
  }

  canFetchFareEstimate() {
    return this.bookingFormModel.offer !== null;
  }

  doesAccountRequireCustomFields() {
    return this.bookingFormModel.businessAccountConfig !== null;
  }

  buildCreateBookingApiDto() {
    return this.bookingFormModel.toCreateApiDto();
  }

  /**
   *
   * @param {String} query
   * @return {Promise<Array<BusinessBookingRecipientV1Dto>>}
   */
  async searchBusinessRecipients(query) {
    // @TODO: Get this info from the local Booking Form Model
    const selectedCorporationId = this.$store.getters["accountsApiStore/accountSelected"];

    console.log("[BookingFormInteractor] fetchPassengersByQuery");
    return this.$store.dispatch("businessBookingRecipient/getBusinessBookingRecipients", {
      dynamicSearch: query,
      page: 0,
      corporationId: selectedCorporationId,
    });
  }

  /**
   * @param {String} businessAccountId
   * @param {Number} page
   * @param {Number} maxPerPage
   * @returns {Promise<FavouritesApiDto[]>}
   */
  async fetchFavouriteAddresses({ businessAccountId, page, maxPerPage }) {
    console.log("[BookingFormInteractor] fetchFavouriteAddresses");
    return this.$store.dispatch("favouritesApiStore/fetchFavouriteAddresses", {
      businessAccountId,
      page,
      maxPerPage,
    });
  }

  /**
   * @param {string} id
   * @returns {Promise<BookingDetailsApiDto>}
   */
  async fetchBookingDetails(id) {
    console.log("[BookingFormInteractor] fetchBookingDetails", id);
    return await this.$store.dispatch("tripsHistory/getBookingDetails", id);
  }

  /**
   * @params {AddressQueryApiDto} query
   * @returns {Promise<AddressSuggestionApiDto[]>}
   */
  async fetchAddressSuggestions(query) {
    console.log("[BookingFormInteractor] fetchAddressSuggestions");

    return this.$store.dispatch("addressSuggestions/getAddressSuggestions", query);
  }

  /**
   * @param {AddressQueryDetailsApiDto} query
   * @returns {Promise<AddressItemDetailApiDto>}
   */
  async fetchAddressDetails(query) {
    console.log("[BookingFormInteractor] fetchAddressDetails");

    return this.$store.dispatch("addressSuggestions/getAddressDetails", query);
  }

  /**
   * @param {PaymentMethodQuery} query
   * @returns {Promise<PaymentMethodApiV1Dto[]>}
   */
  async fetchPaymentMethods(query) {
    console.log("[BookingFormInteractor] fetchPaymentMethods");

    return this.$store.dispatch("paymentMethodsApiStore/getPaymentMethods", query);
  }

  /**
   *
   * @param {string} corporationId
   * @param {AddressItemDetailApiDto} origin
   * @param {AddressItemDetailApiDto} destination
   * @return {Promise<Array<OfferApiDto>>}
   */
  async fetchOffers({ corporationId, origin, destination }) {
    const locale = this.$store.getters["language/selectedLanguage"];
    const offers = await this.$store.dispatch("offersApiStore/getOffers", {
      corporationId,
      lang: locale,
      coords: {
        pickup: {
          latitude: origin.coordinates.latitude,
          longitude: origin.coordinates.longitude,
        },
        dropoff: {
          latitude: destination.coordinates.latitude,
          longitude: destination.coordinates.longitude,
        },
      },
    });

    return offers;
  }

  /**
   * @param {FareEstimateQuery} query
   * @returns {Promise<FareResponceApiDto>}
   */
  async fetchFareEstimate(query) {
    console.log("[BookingFormInteractor] fetchFareEstimate");

    return this.$store.dispatch("fareEstimationApiStore/getFareEstimation", query);
  }

  async fetchFleetAccounts(query) {
    console.log("[BookingFormInteractor] fetchFleetAccounts");
    return this.$store.dispatch("businessBookingRecipient/getFleetAccounts", query);
  }

  /**
   * @param {BookingAccountFieldsQuery} query
   * @returns {Promise<BusinessAccountFieldApiDto[]>}
   */
  async fetchAccountFields(query) {
    console.log("[BookingFormInteractor] fetchAccountFields");

    return this.$store.dispatch("businessBookingRecipient/getAccountFields", query);
  }

  setBusinessRecipient(businessRecipient) {
    this.bookingFormModel.createBookingRecipient = businessRecipient;
    console.log("this.bookingFormModel.createBookingRecipient", this.bookingFormModel.createBookingRecipient);
  }

  /**
   *
   * @return {Promise<{result: BookingApiDto, error}>}
   */
  async createFirstBooking() {
    const requestBody = BookingCreationDtoFactory.fromBookingFormModel(this.bookingFormModel);

    const { result, error } = await SafeAwait.execute(async () => {
      return await this.$store.dispatch('bookingApiStore/requestBooking', requestBody);
    });

    await BusinessBookerEvent.send({
      code: BusinessBookerEventCode.BOOKING_API_REQUEST_PHASE_COMPLETED,
      pageUri: window.location.pathname,
      payload: {
        requestBody,
        result,
        error: error ? HiDash.serializeError(error) : undefined,
      },
    });

    return {
      result,
      error,
    };
  }

  /**
   *
   * @param bookingId
   * @return {Promise<{result: BookingCreationResponseApiDto, error}>}
   */
  async confirmFirstBooking(bookingId) {

    const { error, result } = await SafeAwait.execute(async() => {
      return this.$store.dispatch("bookingApiStore/confirmBooking", bookingId);
    });

    await BusinessBookerEvent.send({
      code: BusinessBookerEventCode.BOOKING_API_CONFIRM_PHASE_COMPLETED,
      pageUri: window.location.pathname,
      payload: {
        bookingId,
        result,
        error: error ? HiDash.serializeError(error) : undefined,
      },
    });

    return {
      error,
      result,
    };
  }

  /**
   *
   * @param booking
   * @return {Promise<{result: BookingCreationResponseApiDto, error}>}
   */
  async createReturnBooking(booking) {
    const requestBody = BookingCreationDtoFactory.fromBookingFormModelToReturnBooking({
      bookingFormModel: this.bookingFormModel,
      firstBookingConfirmResponseDto: booking,
    });

    const { error, result } = await SafeAwait.execute(async () => {
      return this.$store.dispatch("bookingApiStore/requestBooking", requestBody);
    });

    await BusinessBookerEvent.send({
      code: BusinessBookerEventCode.RETURN_BOOKING_API_REQUEST_PHASE_COMPLETED,
      pageUri: window.location.pathname,
      payload: {
        requestBody,
        result,
        error: error ? HiDash.serializeError(error) : undefined,
      },
    });

    return {
      error,
      result,
    };
  }

  /**
   *
   * @param bookingId
   * @return {Promise<{result: BookingApiDto, error}>}
   */
  async confirmReturnBooking(bookingId) {
    console.log("[BookingFormInteractor] confirmReturnBooking", bookingId);

    const { error, result } = await SafeAwait.execute(async () => {
      return this.$store.dispatch("bookingApiStore/confirmBooking", bookingId);
    });

    await BusinessBookerEvent.send({
      code: BusinessBookerEventCode.RETURN_BOOKING_API_CONFIRM_PHASE_COMPLETED,
      pageUri: window.location.pathname,
      payload: {
        bookingId,
        result,
        error: error ? HiDash.serializeError(error) : undefined,
      },
    });

    return {
      error,
      result,
    };
  }

  /**
   * @param {string} authorizationClientSecret
   */
  async confirmPaymentBooking(authorizationClientSecret) {
    console.log("[BookingFormInteractor] confirmPaymentBooking", authorizationClientSecret);
    return this.$store.dispatch("bookingApiStore/confirmPayment", authorizationClientSecret);
  }

  /**
   * @param {string} bookingId
   */
  async cancelBooking(bookingId) {
    console.log("[BookingFormInteractor] cancelBooking", bookingId);
    return this.$store.dispatch("bookingApiStore/cancelBooking", bookingId);
  }
}
